import { Heading } from "@/components/ui/heading";
import { RegularPageLayout } from "@/components/layout/PageLayouts";
import { BLOG_POSTS } from "@/data/blogPosts";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

export default function BlogDetailPage() {
	const { slug } = useParams();
	const navigate = useNavigate();
	const post = BLOG_POSTS.find((item) => item.slug === slug);
	const otherPosts = BLOG_POSTS.filter((item) => item.slug !== slug);

	useEffect(() => {
		if (!post) navigate("/404", { replace: true });
	}, [navigate, post]);

	if (!post) return null;

	function renderParagraph(text: string) {
		const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
		const parts: Array<string | { label: string; href: string }> = [];
		let lastIndex = 0;
		let match = linkPattern.exec(text);

		while (match) {
			const [fullMatch, label, href] = match;
			if (match.index > lastIndex) {
				parts.push(text.slice(lastIndex, match.index));
			}
			parts.push({ label, href });
			lastIndex = match.index + fullMatch.length;
			match = linkPattern.exec(text);
		}

		if (lastIndex < text.length) {
			parts.push(text.slice(lastIndex));
		}

		if (parts.length === 0) return text;

		return parts.map((part, index) => {
			if (typeof part === "string") return <span key={`${index}-${part}`}>{part}</span>;
			return (
				<a
					key={`${index}-${part.href}`}
					href={part.href}
					target="_blank"
					rel="noreferrer"
					className="font-medium text-ink underline underline-offset-2"
				>
					{part.label}
				</a>
			);
		});
	}

	return (
		<RegularPageLayout width="6xl" paddingY="8">
			<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
				<article className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
					<p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
						Published{" "}
						{new Date(post.publishedAt).toLocaleDateString("en-US", {
							year: "numeric",
							month: "long",
							day: "numeric",
						})}
					</p>
					<Heading className="mt-3">{post.title}</Heading>
					<div className="mt-6 space-y-6 text-sm leading-7 text-slate-700 sm:text-base">
						{post.sections.map((section) => (
							<section key={section.heading}>
								<Heading as="h2" className="text-2xl">
									{section.heading}
								</Heading>
								<div className="mt-3 space-y-4">
									{section.paragraphs.map((paragraph) => (
										<p key={paragraph}>{renderParagraph(paragraph)}</p>
									))}
								</div>
							</section>
						))}
					</div>
					<div className="mt-8">
						<Link to="/blog" className="font-medium text-ink underline underline-offset-2">
							Back to blog
						</Link>
					</div>
				</article>

				<aside className="h-fit rounded-2xl border border-slate-200 bg-white p-6">
					<p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
						Other articles
					</p>
					<div className="mt-4 space-y-4">
						{otherPosts.map((item) => (
							<div key={item.slug} className="border-b border-slate-100 pb-4 last:border-b-0 last:pb-0">
								<Link
									to={`/blog/${item.slug}`}
									className="font-semibold text-slate-900 hover:underline underline-offset-2"
								>
									{item.title}
								</Link>
								<p className="mt-1 text-xs text-slate-500">
									{new Date(item.publishedAt).toLocaleDateString("en-US", {
										year: "numeric",
										month: "long",
										day: "numeric",
									})}
								</p>
							</div>
						))}
					</div>
				</aside>
				</div>
			
		</RegularPageLayout>
	);
}
